import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { ModalDrawer } from 'components/ModalDrawer/ModalDrawer'
import type { TOptions } from 'components/MultiSelect/MultiSelect.types'
import { translate } from 'i18n/i18n'
import type { Dispatch, SetStateAction } from 'react'
import { useEffect, useState } from 'react'
import { SharedSettings } from 'settings/SharedSettings'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TProperty } from 'stores/fieldAssetStore/helperTypes'
import { apiFetch } from 'utils/apiFetch'
import { showNotification } from 'utils/showNotification'
import {
  useGroupsAndContacts,
  type Contact,
  type Group,
} from '../../ContactsAndGroups/ContactsAndGroupsContext'
import type { WeeklyGeneralReport, WeeklyTrapCatchesReport, WeeklyTrapIssuesReport } from '../_types'
import { convertAPIReportToInitialValues } from '../_utils/convertAPIReportToInitialValues'
import type { TPestSelected, TReportStartEnd } from '../_utils/formTypesAndStyles'
import type { useReports } from '../_utils/useReports'
import { convertLocalWeeklyGeneralReportToAPIFormat } from '../_utils/weeklyGeneralReportAPIHandling/convertLocalWeeklyGeneralReportToAPIFormat'
import { convertLocalTrapCatchesReportToAPIFormat } from '../_utils/weeklyTrapCatchesReportAPIHandling/convertLocalTrapCatchesReportToAPIFormat'
import { convertLocalTrapIssuesReportToAPIFormat } from '../_utils/weeklyTrapIssuesReportAPIHandling/convertLocalTrapIssuesReportToAPIFormat'
import { ReportContacts } from '../ReportForm/ReportContacts/Contacts'
import { ReportContent } from '../ReportForm/ReportContent/ReportContent'
import { ReportCoverage } from '../ReportForm/ReportCoverage/ReportCoverage'
import { ReportName } from '../ReportForm/ReportName/ReportName'
import { ReportPestTypes } from '../ReportForm/ReportPestTypes/ReportPestTypes'
import { getPermittedWeeklyUpdateReportProperties } from '../ReportForm/ReportProperties/getPermittedWeeklyUpdateReportProperties'
import { ReportProperties } from '../ReportForm/ReportProperties/ReportProperties'
import { ReportScheduling } from '../ReportForm/ReportScheduling/ReportScheduling'
import type { TReportType } from '../ReportForm/ReportType/ReportType'
import {
  ReportType,
  WEEKLY_TRAP_CATCHES_REPORT_TYPE,
  WEEKLY_TRAP_ISSUES_REPORT_TYPE,
  WEEKLY_UPDATE_REPORT_TYPE,
} from '../ReportForm/ReportType/ReportType'
import {
  getReportVariablesForQuery,
  getTrapReportVariablesForQuery,
} from './_utils/getReportVariablesForQuery'

export const ModalDrawerCreateOrEditReport = ({
  opened,
  reportId,
  onClose,
  selectedReport,
  reportType,
  setReportType,
  isDuplicate,
  setIsDuplicate,
  createReportHandler,
  updateReportHandler,
}: {
  opened: boolean
  reportId: string | null
  onClose: () => void
  selectedReport: WeeklyGeneralReport | WeeklyTrapCatchesReport | WeeklyTrapIssuesReport | null
  reportType: TReportType
  setReportType: Dispatch<SetStateAction<TReportType>>
  isDuplicate: boolean
  setIsDuplicate: Dispatch<SetStateAction<boolean>>
  createReportHandler: ReturnType<typeof useReports>['createReportHandler']
  updateReportHandler: ReturnType<typeof useReports>['updateReportHandler']
}) => {
  const [loading, setLoading] = useState(false)
  const initialState = convertAPIReportToInitialValues({ initialValues: selectedReport, reportType })
  const [typeIsValid, setTypeIsValid] = useState(false)
  // State for properties
  const [reportProperties] = useState<TProperty[]>(initialState.reportProperties)
  const [selectedReportProperties, setSelectedReportProperties] = useState<TOptions[]>([])
  const [propertiesIsValid, setPropertiesIsValid] = useState(false)
  // State for Scheduling
  const [dayOfWeek, setDayOfWeek] = useState<string | null>(initialState.dayOfWeek)
  const [scheduleIsValid, setScheduleIsValid] = useState(true)
  // State for Contacts
  const [contacts, setContacts] = useState<Contact[]>(initialState.contacts)
  const [groups, setGroups] = useState<Group[]>(initialState.groups)
  const [notifyOwnerByEmail, setNotifyOwnerByEmail] = useState(initialState.notifyOwnerByEmail)
  // State for Name
  const [contactsIsValid, setContactsIsValid] = useState(true)
  const [nameIsValid, setNameIsValid] = useState(false)
  const [name, setName] = useState<string | null>(initialState.name)
  // State for weather
  const [weather, setWeather] = useState<boolean>(initialState.weather)
  const [weatherStartEnd, setWeatherStartEnd] = useState<TReportStartEnd | null>(initialState.weatherStartEnd)
  // State for fireblight
  const [fireBlight, setFireBlight] = useState<boolean>(initialState.fireBlight)

  const [fireBlightStartEnd, setFireBlightStartEnd] = useState<TReportStartEnd | null>(
    initialState.fireBlightStartEnd,
  )

  // State for pest control
  const [pestsSelected, setPestsSelected] = useState<TPestSelected | null>(initialState.pests)
  // State for water management
  const [evapotranspiration, setEvapotranspiration] = useState<boolean>(initialState.evapotranspiration)

  const [evapotranspirationStartEnd, setEvapotranspirationBorerStartEnd] = useState<TReportStartEnd | null>(
    initialState.evapotranspirationStartEnd,
  )

  // State for coverage
  const [coverageType, setCoverageType] = useState<string>(initialState.coverageType)
  const [coverageIsValid, setCoverageIsValid] = useState<boolean>(true)
  //State for pest type
  const [pestType, setPestType] = useState<string>(initialState.pestType)
  const [pestsIsValid, setPestsIsValid] = useState<boolean>(true)

  const [trapCatchStartEnd, setTrapCatchStartEnd] = useState<TReportStartEnd | null>(
    initialState.trapCatchStartEnd,
  )

  const [trapIssuesStartEnd, setTrapIssuesStartEnd] = useState<TReportStartEnd | null>(
    initialState.trapIssuesStartEnd,
  )

  const [irrigationActivity, setIrrigationActivity] = useState<boolean>(false)

  const [irrigationActivityStartEnd, setIrrigationActivityStartEnd] = useState<TReportStartEnd | null>(
    initialState.irrigationActivityStartEnd,
  )

  const [soilMoisture, setSoilMoisture] = useState<boolean>(false)

  const [soilMoistureStartEnd, setSoilMoistureStartEnd] = useState<TReportStartEnd | null>(
    initialState.soilMoistureStartEnd,
  )

  // State for crop development
  const [chill, setChill] = useState<boolean>(initialState.chill)
  const [chillStartEnd, setChillStartEnd] = useState<TReportStartEnd | null>(initialState.chillStartEnd)
  const [pollination, setPollination] = useState<boolean>(initialState.pollination)

  const [pollinationStartEnd, setPollinationStartEnd] = useState<TReportStartEnd | null>(
    initialState.pollinationStartEnd,
  )

  const [growDegree, setGrowDegree] = useState<boolean>(initialState.growingDegreeDays)

  const [growDegreeStartEnd, setGrowDegreeStartEnd] = useState<TReportStartEnd | null>(
    initialState.growingDegreeDaysStartEnd,
  )

  const [showContentError, setShowContentError] = useState<boolean>(false)
  // STATE FOR KEEPING TRACK OF WHETHER OR NOT WE SENT IT YET
  const [triedToSubmit, setTriedToSubmit] = useState(false)
  const { contacts: allContacts, groups: allContactGroups } = useGroupsAndContacts()
  const [configurationIsValid, setConfigurationIsValid] = useState(true)

  const resetFormState = (reportInitialValues?: typeof selectedReport) => {
    const defaultInitialState = convertAPIReportToInitialValues({
      initialValues: reportInitialValues ?? null,
      reportType,
    })

    setReportType(defaultInitialState.reportType || isDuplicate ? reportType : '')

    setGroups(defaultInitialState.groups)

    setContacts(defaultInitialState.contacts)

    setNotifyOwnerByEmail(defaultInitialState.notifyOwnerByEmail)

    setSelectedReportProperties(defaultInitialState.selectedProperties)

    setDayOfWeek(defaultInitialState.dayOfWeek)

    setName(defaultInitialState.name)

    setWeather(defaultInitialState.weather)

    setFireBlight(defaultInitialState.fireBlight)

    setFireBlightStartEnd(defaultInitialState.fireBlightStartEnd)

    setPestsSelected(defaultInitialState.pests)

    setEvapotranspiration(defaultInitialState.evapotranspiration)

    setEvapotranspirationBorerStartEnd(defaultInitialState.evapotranspirationStartEnd)

    setIrrigationActivity(defaultInitialState.irrigationActivity)

    setIrrigationActivityStartEnd(defaultInitialState.irrigationActivityStartEnd)

    setSoilMoisture(defaultInitialState.soilMoisture)

    setSoilMoistureStartEnd(defaultInitialState.soilMoistureStartEnd)

    setWeatherStartEnd(defaultInitialState.weatherStartEnd)

    setChill(defaultInitialState.chill)

    setChillStartEnd(defaultInitialState.chillStartEnd)

    setPollination(defaultInitialState.pollination)

    setPollinationStartEnd(defaultInitialState.pollinationStartEnd)

    setGrowDegree(defaultInitialState.growingDegreeDays)

    setGrowDegreeStartEnd(defaultInitialState.growingDegreeDaysStartEnd)

    setPestType(defaultInitialState.pestType)

    setCoverageType(defaultInitialState.coverageType)

    setTriedToSubmit(false)

    setIsDuplicate(false)
  }

  const handleOnClose = () => {
    resetFormState()

    onClose()
  }

  useEffect(() => {
    resetFormState(selectedReport)
  }, [selectedReport, opened])

  useEffect(() => {
    const hasPestSelectedTraps = Object.values(pestsSelected ?? {}).some((p) => p?.trapCatches)
    const hasPestSelectedDegreeDays = Object.values(pestsSelected ?? {}).some((p) => p?.degreeDays)

    const hasContent =
      !!weather ||
      !!chill ||
      !!fireBlight ||
      hasPestSelectedTraps ||
      hasPestSelectedDegreeDays ||
      !!pollination ||
      !!growDegree ||
      !!evapotranspiration ||
      !!soilMoisture ||
      !!irrigationActivity

    const isWeeklyUpdateValid =
      reportType === WEEKLY_UPDATE_REPORT_TYPE &&
      typeIsValid &&
      propertiesIsValid &&
      scheduleIsValid &&
      contactsIsValid &&
      nameIsValid &&
      hasContent &&
      !!name

    const isWeeklyTrapCatchesValid =
      reportType === WEEKLY_TRAP_CATCHES_REPORT_TYPE &&
      typeIsValid &&
      coverageIsValid &&
      scheduleIsValid &&
      pestsIsValid &&
      contactsIsValid &&
      nameIsValid &&
      !!name

    const isWeeklyTrapIssuesValid =
      reportType === WEEKLY_TRAP_ISSUES_REPORT_TYPE &&
      typeIsValid &&
      coverageIsValid &&
      scheduleIsValid &&
      pestsIsValid &&
      contactsIsValid &&
      nameIsValid &&
      !!name

    const getIsValid = () => {
      if (reportType === WEEKLY_UPDATE_REPORT_TYPE) return isWeeklyUpdateValid

      if (reportType === WEEKLY_TRAP_CATCHES_REPORT_TYPE) return isWeeklyTrapCatchesValid

      return isWeeklyTrapIssuesValid
    }

    const isValid = getIsValid()

    setShowContentError(propertiesIsValid ? !hasContent : false)

    setConfigurationIsValid(isValid)
  }, [
    reportType,
    typeIsValid,
    propertiesIsValid,
    scheduleIsValid,
    contactsIsValid,
    nameIsValid,
    coverageIsValid,
    pestsIsValid,
    pestType,
    coverageType,
    triedToSubmit,
    name,
    weather,
    chill,
    fireBlight,
    pestsSelected,
    pollination,
    growDegree,
    evapotranspiration,
    soilMoisture,
    irrigationActivity,
  ])

  useEffect(() => {
    const updatedGroups = groups
      .map((group) => {
        const groupInAllGroups = allContactGroups.find((g) => g.id === group.id)

        if (groupInAllGroups) return { ...groupInAllGroups, alertsContactMethods: group.alertsContactMethods }

        return null
      })
      .filter((g) => g !== null)

    const updatedContacts = contacts
      .map((contact) => {
        const contactInAllContacts = allContacts.find((c) => c.id === contact.id)

        if (contactInAllContacts)
          return { ...contactInAllContacts, alertsContactMethods: contact.alertsContactMethods }

        return null
      })
      .filter((c) => c !== null)

    setGroups(updatedGroups as Group[])

    setContacts(updatedContacts as Contact[])
  }, [allContacts, allContactGroups])

  const submitButtonText = reportId
    ? translate.phrases.banyanApp('Update Report')
    : translate.phrases.banyanApp('Create Report')

  const successMessage = reportId
    ? translate.phrases.banyanApp('Report successfully updated')
    : translate.phrases.banyanApp('Report successfully created')

  const errorMessage = reportId
    ? translate.phrases.banyanApp('Failed to update report')
    : translate.phrases.banyanApp('Failed to create report')

  const handleSubmit = async () => {
    if (loading) return

    if (!triedToSubmit) setTriedToSubmit(true)

    if (configurationIsValid) {
      const propertyIds = selectedReportProperties
        ? Object.values(selectedReportProperties).map((obj) => obj.value as string)
        : null

      setLoading(true)

      if (reportType === WEEKLY_UPDATE_REPORT_TYPE) {
        const variables = getReportVariablesForQuery({
          reportId: reportId ?? undefined,
          groups,
          name,
          contacts,
          notifyOwnerByEmail: notifyOwnerByEmail,
          rules: {
            dayOfWeek,
            propertyIds: propertyIds,
            weather: weather ? weatherStartEnd : null,
            fireBlight: fireBlight ? fireBlightStartEnd : null,
            pollination: pollination ? pollinationStartEnd : null,
            growingDegreeDays: growDegree ? growDegreeStartEnd : null,
            evapotranspiration: evapotranspiration ? evapotranspirationStartEnd : null,
            irrigationActivity: irrigationActivity ? irrigationActivityStartEnd : null,
            irrigationPlanner: null,
            soilMoisture: soilMoisture ? soilMoistureStartEnd : null,
            chill: chill ? chillStartEnd : null,
            scout: null,
            pests: pestsSelected,
          },
        })

        const requestBody: routes.LegacyWeeklyGeneralReportsManagement.Request = reportId
          ? {
              legacyWeeklyGeneralReportsToUpdate: {
                [reportId]: convertLocalWeeklyGeneralReportToAPIFormat(variables),
              },
            }
          : {
              legacyWeeklyGeneralReportsToCreate: [convertLocalWeeklyGeneralReportToAPIFormat(variables)],
            }

        const result = await apiFetch({
          url: '/legacy-weekly-general-reports-management',
          body: requestBody,
        })

        const updatedReport = reportId
          ? result?.data?.legacyWeeklyGeneralReportsToUpdate?.[+reportId]
          : Object.values(result?.data?.legacyWeeklyGeneralReportsToCreate ?? {})?.[0]

        if (updatedReport) {
          showNotification({
            type: 'success',
            message: successMessage,
          })

          if (reportId) {
            updateReportHandler(updatedReport, WEEKLY_UPDATE_REPORT_TYPE)
          } else {
            createReportHandler(updatedReport, WEEKLY_UPDATE_REPORT_TYPE)
          }
        } else {
          showNotification({
            type: 'error',
            message: errorMessage,
          })
        }

        setLoading(false)

        onClose()
      } else if (reportType === WEEKLY_TRAP_CATCHES_REPORT_TYPE) {
        const trapVariables = getTrapReportVariablesForQuery({
          reportId: reportId ?? undefined,
          groups,
          name,
          contacts,
          notifyOwnerByEmail: notifyOwnerByEmail,
          rules: {
            dayOfWeek,
            propertyIds: propertyIds,
            startDate: trapCatchStartEnd?.startDate,
            endDate: trapCatchStartEnd?.endDate,
            pests:
              pestType === translate.phrases.banyanApp('Include the following selected pests')
                ? pestsSelected
                : null,
          },
        })

        const requestBody: routes.LegacyWeeklyTrapCatchesReportsManagement.Request = reportId
          ? {
              legacyWeeklyTrapCatchesReportsToUpdate: {
                [reportId]: convertLocalTrapCatchesReportToAPIFormat(trapVariables),
              },
            }
          : {
              legacyWeeklyTrapCatchesReportsToCreate: [
                convertLocalTrapCatchesReportToAPIFormat(trapVariables),
              ],
            }

        const result = await apiFetch({
          url: '/legacy-weekly-trap-catches-reports-management',
          body: requestBody,
        })

        const updatedReport = reportId
          ? result?.data?.legacyWeeklyTrapCatchesReportsToUpdate?.[+reportId]
          : Object.values(result?.data?.legacyWeeklyTrapCatchesReportsToCreate ?? {})?.[0]

        if (updatedReport) {
          showNotification({
            type: 'success',
            message: successMessage,
          })

          if (reportId) {
            updateReportHandler(updatedReport, WEEKLY_TRAP_CATCHES_REPORT_TYPE)
          } else {
            createReportHandler(updatedReport, WEEKLY_TRAP_CATCHES_REPORT_TYPE)
          }
        } else {
          showNotification({
            type: 'error',
            message: errorMessage,
          })
        }

        setLoading(false)

        onClose()
      } else if (reportType === WEEKLY_TRAP_ISSUES_REPORT_TYPE) {
        const trapIssuesVariables = getTrapReportVariablesForQuery({
          reportId: reportId ?? undefined,
          groups,
          name,
          contacts,
          notifyOwnerByEmail: notifyOwnerByEmail,
          rules: {
            dayOfWeek,
            propertyIds: propertyIds,
            startDate: trapIssuesStartEnd?.startDate,
            endDate: trapIssuesStartEnd?.endDate,
            pests:
              pestType === translate.phrases.banyanApp('Include the following selected pests')
                ? pestsSelected
                : null,
          },
        })

        const requestBody: routes.LegacyWeeklyTrapIssuesReportsManagement.Request = reportId
          ? {
              legacyWeeklyTrapIssuesReportsToUpdate: {
                [reportId]: convertLocalTrapIssuesReportToAPIFormat(trapIssuesVariables),
              },
            }
          : {
              legacyWeeklyTrapIssuesReportsToCreate: [
                convertLocalTrapIssuesReportToAPIFormat(trapIssuesVariables),
              ],
            }

        const result = await apiFetch({
          url: '/legacy-weekly-trap-issues-reports-management',
          body: requestBody,
        })

        const updatedReport = reportId
          ? result?.data?.legacyWeeklyTrapIssuesReportsToUpdate?.[+reportId]
          : Object.values(result?.data?.legacyWeeklyTrapIssuesReportsToCreate ?? {})?.[0]

        if (updatedReport) {
          showNotification({
            type: 'success',
            message: successMessage,
          })

          if (reportId) {
            updateReportHandler(updatedReport, WEEKLY_TRAP_ISSUES_REPORT_TYPE)
          } else {
            createReportHandler(updatedReport, WEEKLY_TRAP_ISSUES_REPORT_TYPE)
          }
        } else {
          showNotification({
            type: 'error',
            message: errorMessage,
          })
        }

        setLoading(false)

        onClose()
      }
    }
  }

  return (
    <ModalDrawer
      title={
        reportId
          ? translate.phrases.banyanApp('Update Report - {{reportName}}', { reportName: name })
          : translate.phrases.banyanApp('Create New Report')
      }
      opened={opened}
      onClose={handleOnClose}
      zIndex={SharedSettings.DEFAULT_MODAL_DRAWER_Z_INDEX + 100}
      primaryButtonText={submitButtonText}
      primaryButtonOnPress={handleSubmit}
      primaryButtonDisabled={loading}
    >
      <div css={{ padding: 20 }}>
        <ReportType
          reportId={reportId}
          setReportType={setReportType}
          reportType={reportType}
          triedToSubmit={triedToSubmit}
          setTypeIsValid={setTypeIsValid}
        />
        {reportType || reportId ? (
          <>
            {reportType === WEEKLY_UPDATE_REPORT_TYPE ? (
              <ReportProperties
                properties={getPermittedWeeklyUpdateReportProperties(
                  reportProperties,
                  fieldAssetStore.getState().insects,
                )}
                selectedReportProperties={selectedReportProperties}
                setSelectedReportProperties={setSelectedReportProperties}
                setPropertiesIsValid={setPropertiesIsValid}
                triedToSubmit={triedToSubmit}
              />
            ) : null}
            <ReportScheduling
              dayOfWeek={dayOfWeek}
              setDayOfWeek={setDayOfWeek}
              setScheduleIsValid={setScheduleIsValid}
              triedToSubmit={triedToSubmit}
              pestType={pestType}
              trapCatchStartEnd={
                reportType === WEEKLY_TRAP_ISSUES_REPORT_TYPE ? trapIssuesStartEnd : trapCatchStartEnd
              }
              setTrapCatchStartEnd={
                reportType === WEEKLY_TRAP_ISSUES_REPORT_TYPE ? setTrapIssuesStartEnd : setTrapCatchStartEnd
              }
              reportType={reportType}
            />
            {reportType === WEEKLY_TRAP_CATCHES_REPORT_TYPE ||
            reportType === WEEKLY_TRAP_ISSUES_REPORT_TYPE ? (
              <>
                <ReportPestTypes
                  selectedReportProperties={selectedReportProperties}
                  properties={reportProperties}
                  pestType={pestType}
                  setPestType={setPestType}
                  pestsSelected={pestsSelected}
                  setPestsSelected={setPestsSelected}
                  setPestsIsValid={setPestsIsValid}
                  triedToSubmit={triedToSubmit}
                  reportType={reportType}
                />
                <ReportCoverage
                  selectedReportProperties={selectedReportProperties}
                  properties={reportProperties}
                  setSelectedReportProperties={setSelectedReportProperties}
                  coverageType={coverageType}
                  setCoverageType={setCoverageType}
                  setCoverageIsValid={setCoverageIsValid}
                  triedToSubmit={triedToSubmit}
                />
              </>
            ) : null}
            {reportType === WEEKLY_UPDATE_REPORT_TYPE ? (
              <ReportContent
                weather={weather}
                setWeather={setWeather}
                weatherStartEnd={weatherStartEnd}
                setWeatherStartEnd={setWeatherStartEnd}
                fireBlight={fireBlight}
                setFireBlight={setFireBlight}
                fireBlightStartEnd={fireBlightStartEnd}
                setFireBlightStartEnd={setFireBlightStartEnd}
                pestsSelected={pestsSelected}
                setPestsSelected={setPestsSelected}
                evapotranspiration={evapotranspiration}
                setEvapotranspiration={setEvapotranspiration}
                evapotranspirationStartEnd={evapotranspirationStartEnd}
                setEvapotranspirationBorerStartEnd={setEvapotranspirationBorerStartEnd}
                chill={chill}
                setChill={setChill}
                chillStartEnd={chillStartEnd}
                setChillStartEnd={setChillStartEnd}
                pollination={pollination}
                setPollination={setPollination}
                pollinationStartEnd={pollinationStartEnd}
                setPollinationStartEnd={setPollinationStartEnd}
                growDegree={growDegree}
                setGrowDegree={setGrowDegree}
                growDegreeStartEnd={growDegreeStartEnd}
                setGrowDegreeStartEnd={setGrowDegreeStartEnd}
                selectedReportProperties={selectedReportProperties}
                reportProperties={reportProperties}
                irrigationActivity={irrigationActivity}
                setIrrigationActivity={setIrrigationActivity}
                irrigationActivityStartEnd={irrigationActivityStartEnd}
                setIrrigationActivityStartEnd={setIrrigationActivityStartEnd}
                soilMoisture={soilMoisture}
                setSoilMoisture={setSoilMoisture}
                soilMoistureStartEnd={soilMoistureStartEnd}
                setSoilMoistureStartEnd={setSoilMoistureStartEnd}
                showContentError={showContentError}
                triedToSubmit={triedToSubmit}
              />
            ) : null}
            {/* CONTACTS */}
            <ReportContacts
              allContacts={allContacts}
              allContactGroups={allContactGroups}
              contacts={contacts}
              contactsIsValid={contactsIsValid}
              groups={groups}
              notifyOwnerByEmail={notifyOwnerByEmail}
              setContacts={setContacts}
              setContactsIsValid={setContactsIsValid}
              setGroups={setGroups}
              setNotifyOwnerByEmail={setNotifyOwnerByEmail}
            />

            {/* NAME */}
            <ReportName
              name={name}
              setName={setName}
              setNameIsValid={setNameIsValid}
              triedToSubmit={triedToSubmit}
            />
          </>
        ) : null}
      </div>
    </ModalDrawer>
  )
}
